import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import { useMemo, useState } from 'react';
import { isServer } from 'src/utils/environment';
import { detectUserLocation } from './locationDetection';
import { MarketBannerState } from './types';

const SESSION_STORAGE_KEY = 'purina_market_banner_dismissed';

const hasUserDismissedBanner = () => {
  if (isServer()) return false;
  return sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true';
};

const dismissBannerService = () => {
  if (isServer()) return;
  sessionStorage.setItem(SESSION_STORAGE_KEY, 'true');
};

export const useMarketBanner = () => {
  const [isDismissed, setIsDismissed] = useState(false);

  const locationResult = detectUserLocation();

  const bannerState: MarketBannerState = useMemo(() => {
    // If user has dismissed banner or we're in an error state
    if (hasUserDismissedBanner() || isDismissed) {
      return {
        isVisible: false,
        isDismissed: true,
        detectedMarket: undefined,
      };
    }

    // If still loading or no location result
    if (!locationResult) {
      return {
        isVisible: false,
        isDismissed: false,
        detectedMarket: undefined,
      };
    }

    // Show banner only if user is not in US and we have a market config
    const shouldShowBanner = !locationResult.isUS && !!locationResult.marketConfig;

    return {
      isVisible: shouldShowBanner,
      isDismissed: false,
      detectedMarket: locationResult.marketConfig,
    };
  }, [locationResult, isDismissed]);

  const dismissBanner = (action: 'close' | 'purina_us' | 'my_local_site') => {
    dismissBannerService();
    setIsDismissed(true);
    sendIt({
      event: 'geotarget',
      eventParams: {
        location_assignment: action,
      },
    });
  };

  const goToLocalSite = () => {
    dismissBanner('my_local_site');
  };

  return {
    bannerState,
    dismissBanner,
    goToLocalSite,
  };
};
